import { Minus, Package, Plus } from 'lucide-preact'
import { useRef } from 'preact/hooks'
import { text } from '#contract'
import type { ProductView } from '#contract'
import { useT } from '../../../i18n/index.ts'
import { MAX_COUNT } from './basket.ts'
import { useShop } from './shop-context.ts'
import styles from './products.module.css'

/**
 * A product's count in the basket: "+" alone at 0, else − count +, stopping at the cap. Two
 * buttons, so a gamepad steps through them. "+" stays the same button throughout, and takes
 * the focus when "−" empties the line, so focus never drops out. The count pops on a change.
 */
export function Stepper({ product }: { readonly product: ProductView }) {
  const t = useT()
  const { basket } = useShop()
  const plus = useRef<HTMLButtonElement>(null)
  const count = basket.count(product.item)
  const full = count >= MAX_COUNT
  const name = t(product.name)
  return (
    <span class={styles.stepper} data-empty={count === 0 || undefined}>
      {count > 0 && (
        <button
          type="button"
          class={styles.step}
          aria-label={t(text('shop.less', { name }))}
          onClick={(event) => {
            if (count === 1 && event.currentTarget === document.activeElement) {
              plus.current?.focus()
            }
            basket.remove(product.item)
          }}
        >
          <Minus aria-hidden="true" />
        </button>
      )}
      {count > 0 && (
        <span key={count} class={styles.count}>
          {count}
        </span>
      )}
      <button
        ref={plus}
        type="button"
        class={styles.step}
        aria-label={t(text(count === 0 ? 'shop.add' : 'shop.more', { name }))}
        aria-disabled={full || undefined}
        onClick={() => {
          if (!full) basket.add(product.item)
        }}
      >
        <Plus aria-hidden="true" />
      </button>
    </span>
  )
}

interface ProductIconProps {
  readonly product: ProductView
  readonly class: string | undefined
}

/** The product's baked icon, or a parcel until it's baked. */
export function ProductIcon({ product, class: className }: ProductIconProps) {
  return product.icon ? (
    <img class={className} src={product.icon} alt="" draggable={false} />
  ) : (
    <Package class={className} aria-hidden="true" />
  )
}
